import { useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, CircleCheck, Clock, Loader2, Plus, Sparkles } from 'lucide-react'
import { useAnnouncement } from '../../platform/a11y/useAnnouncement'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useAccounts } from '../accounts/useAccounts'
import { RecurringBackLink } from './BillChrome'
import { pool } from './pool'
import { detectSuggestions, RecurringWriteError } from './recurringApi'
import { frequencyLabel, type Suggestion } from './recurringWire'
import {
  ADD_FAILED,
  DETECT_FAILED,
  DETECT_RATE_LIMITED,
  DISMISS_FAILED,
  NOT_AVAILABLE,
  NOT_PENDING,
} from './suggestionsCopy'
import { useFocusFollowsList } from './useFocusFollowsList'
import { FALLBACK_CURRENCY } from './useSaveRecurring'
import { SuggestionBusyError, useBusySuggestions, useSuggestionWrites } from './useSuggestionWrites'
import { useSuggestions } from './useSuggestions'
import { useSuggestionsShown } from './recurringAnalytics'
import './suggestions.css'

/** S2: Add all runs three accepts at a time. */
const CONCURRENCY = 3

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** "Linked 3 past payments" / "No past payments found" (S2). */
export const linkedCopy = (n: number) => (n === 0 ? 'No past payments found' : `Linked ${plural(n, 'past payment', 'past payments')}`)
const addedCopy = (added: number, linked: number) =>
  `Added ${plural(added, 'recurring payment', 'recurring payments')} and linked ${plural(linked, 'past payment', 'past payments')}.`
const partialCopy = (failed: number) => `${failed} couldn't be added. Try again on each card.`

/** What a 400 or a 404 on a suggestion says, or null when it is neither. */
function staleCopy(e: unknown): string | null {
  if (!(e instanceof RecurringWriteError)) return null
  return e.kind === 'not_pending' ? NOT_PENDING : e.kind === 'removed' ? NOT_AVAILABLE : null
}

type Detect = 'idle' | 'running' | 'rate_limited' | 'failed'

function QueueSkeleton() {
  return (
    <Panel className="sug-queue" data-testid="suggestions-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="sug-row sug-row--skeleton">
          <Skeleton height={34} width={34} />
          <span className="sug-row__text">
            <Skeleton height={13} width="30%" />
            <Skeleton height={10} width="45%" />
          </span>
          <Skeleton height={13} width={70} />
        </div>
      ))}
    </Panel>
  )
}

function Spin() {
  return (
    <span className="spin sug-ico" aria-hidden="true">
      <Icon glyph={Loader2} size={14} />
    </span>
  )
}

/**
 * `/recurring/suggestions` — the review queue (M7-R18–R20, S1–S3).
 *
 * **A GET and nothing else on arrival** (S1): detection runs only from Look
 * again. **Writes never roll back each other**: Add all is N independent
 * accepts, three at a time, and a failure leaves the rest standing — the
 * server is the source of truth, and the refetch at the end shows what landed.
 */
export function Suggestions() {
  const phone = useBreakpoint() === 'phone'
  const toast = useToast()
  const { state, hasMore, loadingMore, loadMore, refetch } = useSuggestions()
  const writes = useSuggestionWrites()
  const inFlight = useBusySuggestions()
  const accounts = useAccounts()
  const [announcement, announce] = useAnnouncement()

  const currencyOf = useMemo(() => {
    const map = new Map<string, string>()
    if (accounts.status === 'success' || accounts.status === 'stale') for (const a of accounts.data) map.set(a.id, a.currency)
    return map
  }, [accounts])
  /**
   * Ruling 48: never `undefined` into `Money`. `null` is "the account is still
   * loading" and the row shows a skeleton; a suggestion with no account, or an
   * account the list does not know, is FALLBACK_CURRENCY (Ruling 44).
   */
  const currencyFor = (s: Suggestion): string | null => {
    if (!s.accountId) return FALLBACK_CURRENCY
    if (accounts.status === 'loading') return null
    return currencyOf.get(s.accountId) ?? FALLBACK_CURRENCY
  }

  const [detect, setDetect] = useState<Detect>('idle')
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set())
  const [bulk, setBulk] = useState(false)
  const [dismissing, setDismissing] = useState<Suggestion | null>(null)

  const rows = state.status === 'success' ? state.data : null
  useSuggestionsShown(rows?.length)
  // The breadcrumb above says only "Recurring", so this heading repeats nothing.
  const heading = useRef<HTMLHeadingElement>(null)
  const focus = useFocusFollowsList(rows ? rows.map((r) => r.id) : null, state.status === 'loading', heading)

  const clearFailed = (ids: string[]) =>
    setFailed((prev) => {
      const next = new Set(prev)
      for (const id of ids) next.delete(id)
      return next
    })

  async function lookAgain() {
    if (detect === 'running') return
    setDetect('running')
    try {
      await detectSuggestions()
    } catch (e) {
      setDetect(e instanceof RecurringWriteError && e.kind === 'rate_limited' ? 'rate_limited' : 'failed')
      return
    }
    setDetect('idle')
    const total = (await refetch()) ?? 0
    announce(total === 0 ? 'Looked again. Nothing new detected.' : `Looked again. ${plural(total, 'suggestion', 'suggestions')}.`)
  }

  async function addOne(s: Suggestion, index: number) {
    if (bulk) return
    clearFailed([s.id])
    focus.toIndex(index)
    try {
      toast.show({ kind: 'success', message: linkedCopy(await writes.accept(s.id)) })
    } catch (e) {
      focus.cancel()
      if (e instanceof SuggestionBusyError) return
      const stale = staleCopy(e)
      if (stale !== null) {
        toast.show({ kind: 'error', message: stale })
      } else {
        setFailed((prev) => new Set(prev).add(s.id))
        toast.show({ kind: 'error', message: ADD_FAILED })
      }
    }
  }

  async function addAll() {
    if (rows === null || rows.length < 2 || bulk) return
    const batch = rows
    setBulk(true)
    clearFailed(batch.map((r) => r.id))
    const results = await pool(batch, CONCURRENCY, (s) => writes.accept(s.id))
    const failures = batch.filter((_, i) => results[i]?.status === 'rejected')
    const linked = results.reduce((n, r) => n + (r.status === 'fulfilled' ? r.value : 0), 0)
    const failedIds = new Set(failures.map((f) => f.id))
    setFailed(failedIds)
    if (failures.length > 0) focus.toFailed(failedIds)
    else focus.toIndex(0)
    // The accepts that landed stay landed; each one's settle already refreshed the list.
    if (failures.length === 0) toast.show({ kind: 'success', message: addedCopy(batch.length, linked) })
    else toast.show({ kind: 'error', message: partialCopy(failures.length) })
    setBulk(false)
  }

  async function confirmDismiss(s: Suggestion, index: number) {
    focus.toIndex(index)
    try {
      await writes.dismiss(s.id)
    } catch (e) {
      const stale = staleCopy(e)
      if (stale === null) {
        focus.cancel()
        throw e
      }
      toast.show({ kind: 'error', message: stale })
    }
    setDismissing(null)
  }

  const lookDisabled = detect === 'running' || bulk
  const lookAgainButton = (
    <Button
      size="page"
      aria-disabled={lookDisabled || undefined}
      aria-busy={detect === 'running' || undefined}
      onClick={() => void lookAgain()}
    >
      {detect === 'running' ? <Spin /> : <span className="sug-ico"><Icon glyph={Sparkles} size={14} /></span>}
      Look again
    </Button>
  )

  const count = rows?.length ?? 0
  const dismissIndex = dismissing && rows ? rows.findIndex((r) => r.id === dismissing.id) : -1

  const body = (
    <Async
        surface="recurring-suggestions"
        state={state}
        skeleton={<QueueSkeleton />}
        empty={{
          icon: (
            <span className="sug-empty__icon">
              <Icon glyph={CircleCheck} size={24} />
            </span>
          ),
          title: 'Nothing new detected',
          body: 'Every repeating charge we found is already tracked.',
          action: { label: 'Look again', onAct: () => void lookAgain() },
        }}
      >
        {(list) => (
          <>
            <Panel className="sug-queue">
              <ul className="sug-list">
                {list.map((s, index) => (
                  <li key={s.id} className="sug-item">
                    <Row
                      s={s}
                      phone={phone}
                      currency={currencyFor(s)}
                      busy={inFlight.has(s.id) || bulk}
                      adding={inFlight.has(s.id)}
                      failed={failed.has(s.id)}
                      addRef={focus.register(s.id)}
                      onAdd={() => void addOne(s, index)}
                      onDismiss={() => setDismissing(s)}
                    />
                  </li>
                ))}
              </ul>
            </Panel>
            {hasMore && (
              <div className="sug-more">
                <Button aria-disabled={loadingMore || undefined} onClick={() => !loadingMore && loadMore()}>
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
    </Async>
  )

  return (
    <div className="sug-page" role="region" aria-label="Suggestions">
      <RecurringBackLink />
      <div className="sug-head">
        <div className="sug-head__identity">
          <h2 ref={heading} tabIndex={-1} className="sug-head__title">
            Suggestions
          </h2>
          {rows && rows.length > 0 && <p className="sug-head__sub">{plural(rows.length, 'repeating charge', 'repeating charges')} spotted</p>}
        </div>
        {rows && rows.length > 0 && (
          <div className={`sug-head__actions${phone ? ' sug-head__actions--phone' : ''}`}>
            {lookAgainButton}
            {count >= 2 && (
              <Button
                variant="primary"
                size="page"
                aria-disabled={bulk || undefined}
                aria-busy={bulk || undefined}
                onClick={() => void addAll()}
              >
                {bulk ? <Spin /> : <span className="sug-ico"><Icon glyph={Plus} size={14} /></span>}
                Add all {count}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Always mounted, so a line arriving later is a change and not an insertion. */}
      <div role="status" className="sug-status">
        {detect === 'rate_limited' && (
          <p className="sug-status__line">
            <Icon glyph={Clock} size={14} />
            {DETECT_RATE_LIMITED}
          </p>
        )}
        {detect === 'failed' && (
          <p className="sug-status__line sug-status__line--failed">
            <Icon glyph={AlertTriangle} size={14} />
            {DETECT_FAILED}
          </p>
        )}
      </div>
      <p className="sug-sr" role="status">
        {announcement}
      </p>

      {state.status === 'empty' ? <Panel className="sug-empty">{body}</Panel> : body}

      {dismissing && (
        <ConfirmDialog
          open
          destructive
          mode="dismiss_recurring_suggestion"
          title="Dismiss this suggestion?"
          body="It won't be suggested again."
          confirmLabel="Dismiss"
          pendingLabel="Dismissing…"
          errorFor={() => DISMISS_FAILED}
          onCancel={() => setDismissing(null)}
          onConfirm={() => confirmDismiss(dismissing, Math.max(dismissIndex, 0))}
        />
      )}
    </div>
  )
}

function Row({
  s,
  phone,
  currency,
  busy,
  adding,
  failed,
  addRef,
  onAdd,
  onDismiss,
}: {
  s: Suggestion
  phone: boolean
  /** `null` while the account that names it is still loading. */
  currency: string | null
  busy: boolean
  adding: boolean
  failed: boolean
  addRef: (el: HTMLButtonElement | null) => void
  onAdd: () => void
  onDismiss: () => void
}): ReactNode {
  const titleId = `sug-${s.id}`
  return (
    <article aria-labelledby={titleId} className={`sug-row${phone ? ' sug-row--phone' : ''}`}>
      <MerchantAvatar merchant={{ id: s.id, name: s.merchant, type: 'other', icon: null, is_global: false }} size="row" />
      <div className="sug-row__text">
        <h3 id={titleId} className="sug-row__name">
          {s.merchant}
        </h3>
        <p className="sug-row__sub">
          {frequencyLabel(s.frequency)} · seen {s.seenCount} {s.seenCount === 1 ? 'time' : 'times'}
        </p>
        {failed && (
          <p className="sug-row__failed">
            <Icon glyph={AlertTriangle} size={12} />
            {ADD_FAILED}
          </p>
        )}
      </div>
      <div className="sug-row__amount">
        <span className="sug-row__figure">
          {currency === null ? <Skeleton height={13} width={56} /> : <Money cents={s.averageCents} currency={currency} />}
        </span>
        <span className="sug-row__avg">avg</span>
      </div>
      <div className="sug-row__actions">
        <Button
          ref={addRef}
          size="panel"
          aria-label={`Add ${s.merchant}`}
          aria-disabled={busy || undefined}
          aria-busy={adding || undefined}
          onClick={() => !busy && onAdd()}
        >
          {adding ? <Spin /> : <span className="sug-ico"><Icon glyph={Plus} size={14} /></span>}
          Add
        </Button>
        <Button
          variant="outline"
          size="panel"
          aria-label={`Dismiss ${s.merchant}`}
          aria-disabled={busy || undefined}
          onClick={() => !busy && onDismiss()}
        >
          Dismiss
        </Button>
      </div>
    </article>
  )
}
